import React, { useState, useEffect, useCallback } from "react";
import useApi from "../../hooks/useApi";
import checkIfUserHasAccess from "../common/UserCheckAccess";

const FloatingItemInformation = ({
  itemInfo,
  showSidebar,
  currentLocation,
  productCode,
  companyCode,
  API_BASE_URL,
  department,
  onStockInhandUpdate,
  onItemInfoUpdate,
  onClose,
}) => {
  const [itemData, setItemData] = useState(null);
  const [branchData, setBranchData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { request } = useApi();

  useEffect(() => {
    if (itemData && onStockInhandUpdate) {
      onStockInhandUpdate(itemData.Br_Qty);
    }
    if (itemData && onItemInfoUpdate) {
      onItemInfoUpdate({
        blockPrice: itemData.Block_Price,
        costAvg: itemData.Cost_Avg,
        dac: itemData.DAC,
      });
    }
  }, [itemData, onStockInhandUpdate, onItemInfoUpdate]);

  useEffect(() => {
    const fetchItemInfo = async () => {
      if (!productCode || !companyCode || !API_BASE_URL || !showSidebar) {
        setItemData(null);
        return;
      }

      setLoading(true);
      setError(null);
      setItemData(null);

      try {
        const url = `${API_BASE_URL}/api/CRMItemInfo/${companyCode}/${encodeURIComponent(productCode)}/${(currentLocation || "-").trim()}/-`;
        console.log("URL FLOATING", url)
        const response = await request({
          url: url,
          method: "GET",
          timeout: 5000,
        });

        if (response && response.length > 0) {
          setItemData(response[0]);
        } else {
          setItemData(null);
        }
      } catch (err) {
        console.error("Error fetching item info:", err);
        setError("Failed to load item data");
        setItemData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchItemInfo();
  }, [productCode, companyCode, currentLocation, API_BASE_URL, showSidebar]);

  useEffect(() => {
    const fetchBranchData = async () => {
      if (!productCode || !API_BASE_URL || !showSidebar) {
        setBranchData([]);
        return;
      }
      setBranchData([]);
      // const encodedProductCode = encodeURIComponent(productCode);
      try {
        const url = `${API_BASE_URL}/api/CRMStockList/${companyCode}/LOCATIONSTOCK/${encodeURIComponent(productCode)}/-/-`;
        console.log("URL STOCK FLOATING", url)
        const response = await request({
          url: url,
          method: "GET",
          timeout: 5000,
        });
        setBranchData(response || []);
        console.log("Location stock LOC", response)
      } catch (err) {
        console.error("Error fetching branch data:", err);
        setBranchData([]);
      }
    };

    fetchBranchData();
  }, [productCode, API_BASE_URL, showSidebar, currentLocation]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape" && showSidebar && onClose) {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    },
    [showSidebar, onClose]
  );

  useEffect(() => {
    if (!showSidebar) return;
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [showSidebar, handleKeyDown]);

  const currentUserAccess = JSON.parse(localStorage.getItem("currentUser") || "[]")?.[0]?.useraccess || "";
  const shouldHideCost = ["-AC", "-SW", "-ST"].some(code => currentUserAccess.includes(code));

  const sortedBranchData = [...branchData].sort((a, b) => {
    // console.log("Sort brnahc data", sortedBranchData)
    const getSortWeight = (qty) => {
      if (qty > 0) return 1; // Highest priority
      if (qty < 0) return 2; // Middle priority
      return 3; // Lowest priority (0)
    };

    const weightA = getSortWeight(a.Qty);
    const weightB = getSortWeight(b.Qty);

    // If weights are different, sort by weight (1 comes first)
    if (weightA !== weightB) {
      return weightA - weightB;
    }

    // If both are in the same category (e.g., both > 0),
    // sort them descending by value (optional, but looks cleaner)
    return b.Qty - a.Qty;
  });
  return (
    <div
      className={`fixed top-0 right-0 w-40 h-full bg-white shadow-lg border-l border-gray-300 z-40 transform transition-transform duration-300 ease-in-out overflow-auto scrollbar-thin ${showSidebar ? "translate-x-0" : "translate-x-full"
        }`}
    >
        <div className="p-2 text-xs">
        <div className="flex items-center justify-between mb-2 border-b pb-1">
          <h3 className="text-xs font-bold text-gray-800">
            Stock Info - {currentLocation}
          </h3>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-red-500 transition-colors p-0.5 rounded hover:bg-gray-100"
              title="Close (Esc)"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {loading && (
          <div className="text-center text-gray-500 text-xs py-2">
            Loading...
          </div>
        )}

        {error && (
          <div className="text-center text-red-500 text-xs py-2">{error}</div>
        )}

        {!loading && !error && itemData && (
          <div className="space-y-1.5">
            <div className="flex justify-between items-center py-1 border-b border-gray-100">
              <span className="text-gray-600 text-xs">Br Qty:</span>
              <span className="font-bold text-base text-red-600 bg-red-100 px-1 py-0.5 rounded">
                {itemData.Br_Qty || "0"}
              </span>
            </div>

            <div className="flex justify-between items-center py-1 border-b border-gray-100">
              <span className="text-gray-600 text-xs">Ttl Qty:</span>
              <span className="font-bold text-base text-blue-600 bg-blue-100 px-1 py-0.5 rounded">
                {itemData.Ttl_Qty || "0"}
              </span>
            </div>

            <div className="flex justify-between items-center py-1 border-b border-gray-100">
              <span className="text-gray-600 text-xs">DO Pend:</span>
              <span className="font-bold text-sm bg-gray-100 px-1 py-0.5 rounded">
                {itemData.DO_PEND || "0"}
              </span>
            </div>

            <div className="flex justify-between items-center py-1 border-b border-gray-100">
              <span className="text-gray-600 text-xs">Ord Pend:</span>
              <span className="font-bold text-sm bg-orange-100 px-1 py-0.5 rounded">
                {itemData.ORD_PEND || "0"}
              </span>
            </div>

            <div className="flex justify-between items-center py-1 border-b border-gray-100">
              <span className="text-gray-600 text-xs">PO Pend:</span>
              <span className="font-bold text-sm bg-teal-100 px-1 py-0.5 rounded">
                {itemData.PO_PEND || "0"}
              </span>
            </div>
            {!shouldHideCost && (
              <div className="flex justify-between items-center py-1 border-b border-gray-100">
                <span className="text-gray-600 text-xs">Cost Avg:</span>
                <span className="font-semibold text-xs bg-yellow-100 px-1 py-0.5 rounded">
                  {itemData.Cost_Avg?.toFixed(2) || "0.00"}
                </span>
              </div>
            )}

            <div className="flex justify-between items-center py-1 border-b border-gray-100">
              <span className="text-gray-600 text-xs">Retail:</span>
              <span className="font-semibold text-xs bg-green-100 px-1 py-0.5 rounded">
                {itemData.Retail_Price?.toFixed(2) || "0.00"}
              </span>
            </div>

            <div className="flex justify-between items-center py-1 border-b border-gray-100">
              <span className="text-gray-600 text-xs">Reseller:</span>
              <span className="font-semibold text-xs bg-purple-100 px-1 py-0.5 rounded">
                {itemData.Reseller_Price?.toFixed(2) || "0.000"}
              </span>
            </div>

            <div className="flex justify-between items-center py-1 border-b border-gray-100">
              <span className="text-gray-600 text-xs">Export:</span>
              <span className="font-semibold text-xs bg-indigo-100 px-1 py-0.5 rounded">
                {itemData.Export_Price?.toFixed(2) || "0.00"}
              </span>
            </div>

            <div className="flex justify-between items-center py-1 border-b border-gray-100">
              <span className="text-gray-600 text-xs">Block:</span>
              <span className="font-semibold text-xs bg-pink-100 px-1 py-0.5 rounded">
                {itemData.Block_Price?.toFixed(2) || "0.000"}
              </span>
            </div>
            {checkIfUserHasAccess("NR") && (
              <div className="flex justify-between items-center py-1 border-b border-gray-100">
                <span className="text-gray-600 text-xs">LS Price:</span>
                <span className="font-semibold text-xs bg-blue-100 px-1 py-0.5 rounded">
                  {itemData.LSPrice?.toFixed(2) || "0.000"}
                </span>
              </div>
            )}

            <div className="flex justify-between items-center py-1 border-b border-gray-100">
              <span className="text-gray-600 text-xs">LCS Price:</span>
              <span className="font-semibold text-xs bg-cyan-100 px-1 py-0.5 rounded">
                {itemData.LCSPrice?.toFixed(2) || "N/A"}
              </span>
            </div>

            {itemData.Bin && (
              <div className="flex justify-between items-center py-1 border-b border-gray-100">
                <span className="text-gray-600 text-xs">Bin:</span>
                <span className="font-semibold text-xs bg-gray-100 px-1 py-0.5 rounded">
                  {itemData.Bin}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="mt-3">
          <h4 className="text-xs font-bold mb-1 border-b pb-1 text-gray-800">
            Location Stock
          </h4>

          <div className="space-y-1">
            {sortedBranchData?.map((branch, index) => {
              const [locCode] = branch?.LOC;

              return (
                <div
                  key={`${branch.LOC}-${index}`}
                  className={`p-2 rounded border text-[11px] leading-tight
    ${branch.Qty > 0
                      ? "border-green-200 bg-green-50"
                      : "border-red-200 bg-red-50"
                    }`}
                >
                  {/* Location */}
                  <div
                    className="font-semibold text-blue-700 truncate"
                    title={branch.LOC}
                  >
                    {branch.LOC}
                  </div>

                  {/* Qty & Bin */}
                  <div className="flex items-center justify-between mt-1">
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">Qty:</span>
                      <span
                        className={`font-medium ${branch.Qty > 0 ? "text-green-700" : "text-red-600"
                          }`}
                      >
                        {branch.Qty}
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">Bin:</span>
                      <span className="font-medium truncate max-w-12">
                        {branch.BIN || "-"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}

            {sortedBranchData.length === 0 && !loading && (
              <div className="text-center text-gray-500 text-xs py-2">
                No location data available.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingItemInformation;
